import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Edit,
  Image as ImageIcon,
  Loader,
  MoreVertical,
  Search,
  Trash2
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import Background from "../components/background.tsx";
import Header from "../components/header.tsx";
import DeleteConfirmationModal from "../components/history/delete-confirmation-modal.tsx";
import EditDetailModal from "../components/history/edit-detail-modal.tsx";
import ImagePreviewModal from "../components/history/image-preview-modal.tsx";
import Sidebar from "../components/sidebar.tsx";
import TechnoArtLogo from "../components/technoArtLogo.tsx";

// BUG search don't work on page 2+

interface DetectionRecord {
  gateLocation: any;
  id: string | number;
  timestampEntry: string;
  timestampExit?: string;
  originalImageEntry?: string;
  plateImageEntry?: string;
  vehicleImageEntry?: string;
  originalImageExit?: string;
  plateImageExit?: string;
  annotatedImageEntry?: string;
  annotatedImageExit?: string;
  plateNumber: string;
  vehicleType: string;
  region: string;
  plateType: string;
  confidence: number | null;
  detection_details?: any;
  detection_type?: string;
  file_path?: string;
  original_path?: string;
  annotated_path?: string;
  plateStatus: "blacklist" | "whitelist" | "unclassified" | null;  // Improve typing
  processing_time?: string;
  image_resolution?: string;
}

const DetectionHistoryPage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectionRecords, setDetectionRecords] = useState<DetectionRecord[]>(
    []
  );
  const [vehicleTypeFilter, setVehicleTypeFilter] = useState<string>("all");
  const [plateTypeFilter, setPlateTypeFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  // Update confirmModal state type
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    recordId?: string | number;  // Add this property
    useDirectApi?: boolean;      // Add this property
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => { },
  });

  const [previewImage, setPreviewImage] = useState<string | undefined>(
    undefined
  );

  const [editModal, setEditModal] = useState<{ open: boolean; record: DetectionRecord | null }>({
    open: false,
    record: null,
  });

  const recordsPerPage = 5;

  // New state for responsive view mode
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('table');

  // Function to fetch detection records from API
  const fetchDetectionRecords = useCallback(
    async (page: number = 1) => {
      setLoading(true);
      setError(null);

      try {
        const offset = (page - 1) * recordsPerPage;
        // Use the endpoint defined in detections.py
        let url = `https://galaxy-alpr-be.onrender.com/detections?limit=${recordsPerPage}&offset=${offset}`;

        // Add filters if selected
        if (vehicleTypeFilter !== "all") {
          url += `&vehicleType=${vehicleTypeFilter}`;
        }

        if (plateTypeFilter !== "all") {
          url += `&plateType=${plateTypeFilter}`;
        }

        // Always include search parameter if it exists, regardless of page
        if (searchQuery.trim()) {
          url += `&search=${encodeURIComponent(searchQuery.trim())}`;
        }

        console.log(`[Galaxy-ALPR-DEBUG] API Request URL: ${url}`);
        console.log(`[Galaxy-ALPR-DEBUG] Current state - page:${currentPage}, search:"${searchQuery}", vehicleType:${vehicleTypeFilter}, plateType:${plateTypeFilter}`);

        const response = await fetch(url);

        // Log the response status for debugging
        console.log(`[Galaxy-ALPR] API response status: ${response.status}`);

        if (!response.ok) {
          throw new Error(
            `Error fetching detection records: ${response.statusText}`
          );
        }

        const data = await response.json();

        // Log response data for debugging
        console.log(`[Galaxy-ALPR] API returned ${data.detections?.length || 0} records of total ${data.count}`);

        if (data.error) {
          throw new Error(data.error || "Unknown error fetching detection records");
        }

        // Detailed logging for debug purposes
        console.log(`API returned ${data.detections?.length || 0} records of total ${data.count}`);
        console.log(`Calculating total pages: ${Math.ceil((data.count || 0) / recordsPerPage)}`);

        // Transform API data to match our DetectionRecord interface
        const transformedRecords: DetectionRecord[] = data.detections.map(
          (item: any) => {
            // Get plate info from detection_details if available
            const details = item.detection_details || {};

            // Use the correct path for original images
            let originalImageUrl = item.original_path || "";
            if (originalImageUrl && !originalImageUrl.startsWith('/outputs') && !originalImageUrl.startsWith('http')) {
              // For older format paths, extract just the filename
              const originalFilename = originalImageUrl.split(/[/\\]/).pop();
              originalImageUrl = `/uploads/${originalFilename}`;
            }

            // Get the annotated image path from API response
            const annotatedImagePath = item.annotated_path || "";

            // IMPORTANT: Use the file_path directly from API instead of conditionally choosing
            // This ensures we show the plate image path selected by the backend
            const detectionFilePath = item.file_path || "";

            // Create timestamps - remove random exit time generation
            const entryTime = new Date(item.timestamp);
            const exitTime = undefined; // No exit time implementation yet

            // Get precise plate type from the database
            const plateType = details.plate_type || "unknown";

            // Use OCR confidence instead of detection confidence
            const ocrConfidence = details.ocr_confidence !== undefined ? details.ocr_confidence * 100 : null;

            // Get session data
            const processingTime = item.processing_time || details.processing_time || null;
            const imageResolution = item.image_resolution || details.image_resolution || null;

            // Handle plate status properly - use API data directly without determining status in frontend
            const plateStatus = details.plate_status || "unclassified";

            return {
              id: item.id,
              timestampEntry: entryTime
                .toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                }).replace(",", ","),
              timestampExit: undefined, // Don't generate exit timestamp
              originalImageEntry: originalImageUrl,
              plateImageEntry: detectionFilePath,
              originalImageExit: originalImageUrl,
              plateImageExit: detectionFilePath,
              annotatedImageEntry: annotatedImagePath, // Add annotated image path
              annotatedImageExit: annotatedImagePath, // Add annotated image path (same for demo)
              plateNumber: details.plate_text || "unknown",
              vehicleType: details.vehicle_type || "unknown",
              region: details.region || "unknown",
              plateType: plateType,
              confidence: ocrConfidence,
              detection_type: item.detection_type,
              file_path: detectionFilePath,
              original_path: originalImageUrl,
              annotated_path: annotatedImagePath,
              plateStatus: plateStatus, // Use the API-provided status directly
              processing_time: processingTime,
              image_resolution: imageResolution,
            };
          }
        );

        setDetectionRecords(transformedRecords);
        setTotalEntries(data.count || 0);
        setTotalPages(Math.max(1, Math.ceil((data.count || 0) / recordsPerPage)));

        // Additional debug info
        console.log(`Page ${page} of ${Math.max(1, Math.ceil((data.count || 0) / recordsPerPage))}`);
        console.log(`Received ${transformedRecords.length} records for display`);

      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error occurred");
        console.error("Error fetching detection records:", err);
      } finally {
        setLoading(false);
      }
    },
    [vehicleTypeFilter, plateTypeFilter, searchQuery, currentPage]
  );

  // Handle search - completely revised to fix empty query issues
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log(`[Galaxy-ALPR] Search submitted: "${searchQuery}" - Resetting to page 1`);

    // Reset to page 1 when searching
    setCurrentPage(1);

    // No need to call fetchDetectionRecords here as it will be triggered by the effect
  };

  // Additional handler for search input changes - improved with better empty state handling
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    // If search field changes, always go back to page 1
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Fetch data when component mounts or filters change - revised for better search handling
  useEffect(() => {
    console.log(`[Galaxy-ALPR] Effect running - Page: ${currentPage}, Search: "${searchQuery}"`);

    // Add a small delay to allow state updates to complete
    const timer = setTimeout(() => {
      fetchDetectionRecords(currentPage);
    }, 50);

    return () => clearTimeout(timer);
  }, [currentPage, vehicleTypeFilter, plateTypeFilter, searchQuery, fetchDetectionRecords]);

  // New function to automatically set view mode based on screen width
  const updateViewModeBasedOnScreenSize = useCallback(() => {
    const isSmallScreen = window.innerWidth < 1024;
    setViewMode(isSmallScreen ? 'cards' : 'table');
  }, []);

  // Initialize and listen for window resize to set view mode
  useEffect(() => {
    updateViewModeBasedOnScreenSize();
    window.addEventListener('resize', updateViewModeBasedOnScreenSize);
    return () => window.removeEventListener('resize', updateViewModeBasedOnScreenSize);
  }, [updateViewModeBasedOnScreenSize]);

  const getPlateTypeColor = (plateType: string) => {
    switch (plateType.toLowerCase()) {
      case "military":
        return "bg-green-600 text-white";
      case "private":
        return "bg-cyan-500 text-white";
      case "public":
        return "bg-blue-500 text-white";
      case "government":
        return "bg-purple-500 text-white";
      case "police":
        return "bg-indigo-600 text-white";
      case "diplomatic":
        return "bg-yellow-600 text-white";
      case "free_trade":
        return "bg-orange-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  // Improved status color function with better handling of edge cases
  const getStatusColor = (status: string | null) => {
    if (!status) return "bg-gray-400 text-white";

    switch (status.toLowerCase()) {
      case "blacklist":
        return "bg-red-500 text-white";
      case "whitelist":
        return "bg-green-500 text-white";
      case "unclassified":
      default:
        return "bg-gray-400 text-white";
    }
  };

  const getConfidenceColor = (confidence: number | null) => {
    if (confidence === null) return "text-gray-600";
    if (confidence >= 85) return "text-green-600";
    if (confidence >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      console.log(`[Galaxy-ALPR-DEBUG] Navigating to previous page: ${prevPage}, with search: "${searchQuery}"`);
      setCurrentPage(prevPage);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      console.log(`[Galaxy-ALPR-DEBUG] Navigating to next page: ${nextPage}, with search: "${searchQuery}"`);
      setCurrentPage(nextPage);
    }
  };

  const goToPage = (pageNum: number) => {
    console.log(`[Galaxy-ALPR-DEBUG] Directly navigating to page: ${pageNum}, with search: "${searchQuery}"`);
    setCurrentPage(pageNum);
  };

  const goToFirstPage = () => {
    console.log(`[Galaxy-ALPR-DEBUG] Navigating to first page with search: "${searchQuery}"`);
    setCurrentPage(1);
  };

  const goToLastPage = () => {
    console.log(`[Galaxy-ALPR-DEBUG] Navigating to last page: ${totalPages}, with search: "${searchQuery}"`);
    setCurrentPage(totalPages);
  };

  // Function to get page numbers with unique keys - fixed to eliminate duplicate keys
  const getPageNumbers = () => {
    const maxVisibleButtons = 5;
    let pageArray: number[] = [];

    // Handle case where totalPages is 0 (no results)
    if (totalPages <= 0) {
      return [1]; // Just show page 1 if there are no results
    }

    if (totalPages <= maxVisibleButtons) {
      // If we have fewer pages than the max we want to show, display all pages
      pageArray = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // Always include first and last page
      pageArray = [1];

      // Calculate the range around current page
      let startPage = Math.max(2, currentPage - Math.floor((maxVisibleButtons - 3) / 2));
      let endPage = Math.min(totalPages - 1, startPage + maxVisibleButtons - 4);

      // Adjust the start if we're near the end
      if (endPage === totalPages - 1) {
        startPage = Math.max(2, endPage - (maxVisibleButtons - 4));
      }

      // Add ellipsis indicator if needed (using negative numbers as markers)
      if (startPage > 2) {
        pageArray.push(-1); // Left ellipsis marker
      }

      // Add the page numbers around current page
      for (let i = startPage; i <= endPage; i++) {
        pageArray.push(i);
      }

      // Add right ellipsis if needed
      if (endPage < totalPages - 1) {
        pageArray.push(-2); // Right ellipsis marker
      }

      // Add the last page
      pageArray.push(totalPages);
    }

    return pageArray;
  };

  const pageNumbers = getPageNumbers();

  const toggleMenu = (id: string | number) => {
    setMenuOpenId((prev) => (prev === id.toString() ? null : id.toString()));
  };

  // Helper function to determine if menu should open upward
  const shouldOpenUpward = (recordIndex: number) => {
    return recordIndex >= detectionRecords.length - 2;
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (detectionRecords.length === 0) return;

    const headers = [
      "ID",
      "Entry Timestamp",
      "Exit Timestamp",
      "Plate Number",
      "Vehicle Type",
      "Plate Region",
      "Plate Type",
      "OCR Confidence (%)",
    ];

    const csvData = detectionRecords.map((record) => [
      record.id,
      record.timestampEntry,
      record.timestampExit || "N/A",
      record.plateNumber,
      record.vehicleType,
      record.region,
      record.plateType,
      record.confidence !== null && typeof record.confidence === "number" ? record.confidence.toFixed(1) : "N/A",
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `detection-history-${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to handle image URLs - Updated to handle annotated images
  const getImageUrl = (url: string | undefined, type: string = ''): string | undefined => {
    if (!url) return undefined;

    // If it's already a full URL, return it
    if (url.startsWith('http')) return url;

    // Make sure paths that start with /outputs go directly to outputs endpoint
    if (url.startsWith('/outputs')) {
      return `https://galaxy-alpr-be.onrender.com${url}`;
    }

    // Special handling for annotated images
    if (type === 'annotated') {
      // Most annotated images will be in the root vehicles or plates folder
      if (url.includes('vehicles')) {
        const filename = url.split('/').pop();
        return `https://galaxy-alpr-be.onrender.com/outputs/vehicles/${filename}`;
      } else if (url.includes('plates')) {
        const filename = url.split('/').pop();
        return `https://galaxy-alpr-be.onrender.com/outputs/plates/${filename}`;
      }
    }

    // Handle paths that need the uploads endpoint
    if (url.startsWith('/uploads') || type === 'original' || type === 'uploaded') {
      // Extract filename from the path to avoid duplicating path segments
      const filename = url.split('/').pop();
      return `https://galaxy-alpr-be.onrender.com/uploads/${filename}`;
    }

    // Legacy handling - if path doesn't have expected format but contains certain directories
    if (url.includes('/vehicles/') || url.includes('/plates/')) {
      const parts = url.split('/');
      const directory = parts.includes('vehicles') ? 'vehicles' : 'plates';
      const subdir = parts.includes('crops') ? 'crops' : '';
      const filename = parts.pop();

      if (subdir) {
        return `https://galaxy-alpr-be.onrender.com/outputs/${directory}/${subdir}/${filename}`;
      }
      return `https://galaxy-alpr-be.onrender.com/outputs/${directory}/${filename}`;
    }

    // Default fallback - add the host prefix
    return `https://galaxy-alpr-be.onrender.com${url}`;
  };

  // Add image error handling function with improved reporting
  const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
    type: string = ''
  ) => {
    const imgElement = e.currentTarget;
    console.error(`Failed to load image (${type}):`, imgElement.src);
    imgElement.src = "https://via.placeholder.com/80x60?text=No+Image";
    imgElement.alt = "Image not found";
    imgElement.onerror = null; // Prevent infinite loop
  };

  // Add a new function to handle updates
  const handleRecordUpdate = (updatedRecord: any) => {
    console.log("Record updated:", updatedRecord);
    // Refresh the data after an update
    fetchDetectionRecords(currentPage);
  };

  // Function to handle delete confirmation
  const handleDeleteClick = (record: DetectionRecord) => {
    // Get the actual plate ID from detection_details
    const plateId = record.detection_details?.id || record.id;

    console.log(`Preparing to delete plate with ID: ${plateId}`);

    setConfirmModal({
      isOpen: true,
      title: "Delete Record",
      message: `Are you sure you want to delete the record for ${record.plateNumber}?`,
      recordId: plateId,
      useDirectApi: true,
      onConfirm: () => {
        // After successful deletion, refresh the data
        fetchDetectionRecords(currentPage);
      },
    });
    setMenuOpenId(null);
  };

  // New helper for truncating text with tooltip
  const truncateText = (text: string, maxLength: number = 20) => {
    if (!text || text.length <= maxLength) return text;
    return (
      <div className="truncate" title={text}>
        {text.substring(0, maxLength)}...
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = () => {
      if (menuOpenId !== null) setMenuOpenId(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [menuOpenId]);

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <Background />

      <div className="flex relative z-10">
        {/* Sidebar */}
        <Sidebar />

        {/* Main Content */}
        <div className="flex-1 overflow-x-hidden">
          {/* Header */}
          <Header />

          {/* Content Area */}
          <div className="p-4 md:p-8">
            <div className="max-w-full mx-auto">
              {/* HEADER BAR */}
              <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-t-2xl p-4 md:p-6 shadow-lg">
                <div className="flex flex-col lg:flex-row gap-4 justify-between">
                  <h1 className="text-2xl font-bold text-gray-800">
                    Detection History
                  </h1>

                  {/* Filter controls - made responsive */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex gap-3 w-full lg:w-auto">
                    <select
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={vehicleTypeFilter}
                      onChange={(e) => {
                        setVehicleTypeFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="all">All Vehicle Types</option>
                      <option value="Car">Car</option>
                      <option value="Motorcycle">Motorcycle</option>
                    </select>

                    <select
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={plateTypeFilter}
                      onChange={(e) => {
                        setPlateTypeFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="all">All Plate Types</option>
                      <option value="private">Private</option>
                      <option value="public">Public</option>
                      <option value="government">Government</option>
                      <option value="police">Police</option>
                      <option value="military">Military</option>
                      <option value="diplomatic">Diplomatic</option>
                      <option value="free_trade">Free Trade</option>
                    </select>

                    <form onSubmit={handleSearch} className="relative sm:col-span-2 lg:col-span-1">
                      <Search
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                        size={16}
                      />
                      <input
                        type="text"
                        placeholder="Search plate number..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                      />
                    </form>

                    <button
                      className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={exportToCSV}
                      disabled={detectionRecords.length === 0}
                    >
                      <Download size={16} />
                      <span className="hidden sm:inline">Export</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* TABLE CONTENT AREA WITH RESPONSIVE LAYOUT */}
              <div className="bg-white bg-opacity-95 backdrop-blur-sm shadow-lg overflow-hidden">
                {loading ? (
                  <div className="flex justify-center items-center py-20">
                    <Loader className="animate-spin mr-2" size={24} />
                    <span>Loading detection records...</span>
                  </div>
                ) : error ? (
                  <div className="p-6 text-center text-red-500">
                    <p>Error: {error}</p>
                    <button
                      className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                      onClick={() => fetchDetectionRecords(currentPage)}
                    >
                      Retry
                    </button>
                  </div>
                ) : detectionRecords.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    No detection records found.
                  </div>
                ) : viewMode === 'table' ? (
                  // DESKTOP TABLE VIEW
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          {[
                            "Timestamp",
                            "Plate Images",
                            "Plate Details",
                            "Vehicle Info",
                            "Actions",
                          ].map((head) => (
                            <th
                              key={head}
                              className="px-4 py-3 text-left text-sm font-medium text-gray-600"
                            >
                              {head}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {detectionRecords.map((record, index) => (
                          <tr key={record.id} className="hover:bg-gray-50">
                            {/* Column 1: Timestamps */}
                            <td className="px-4 py-4 whitespace-nowrap align-top">
                              <div className="text-sm font-medium text-gray-800">Entry:</div>
                              <div className="text-sm text-gray-600">{record.timestampEntry}</div>
                              {record.timestampExit && (
                                <>
                                  <div className="text-sm font-medium text-gray-800 mt-2">Exit:</div>
                                  <div className="text-sm text-gray-600">{record.timestampExit}</div>
                                </>
                              )}
                            </td>

                            {/* Column 2: Images */}
                            <td className="px-4 py-4 align-top">
                              <div className="flex space-x-2">
                                {/* Annotated Image */}
                                <div className="flex flex-col items-center">
                                  <div className="text-xs text-gray-500 mb-1">Annotated</div>
                                  {record.annotatedImageEntry ? (
                                    <img
                                      src={getImageUrl(record.annotated_path, 'annotated')}
                                      alt="Annotated Plate"
                                      className="w-20 h-14 object-cover rounded-lg bg-gray-200 cursor-pointer"
                                      onError={(e) => handleImageError(e, 'annotated-entry')}
                                      onClick={() => setPreviewImage(getImageUrl(record.annotated_path, 'annotated'))}
                                    />
                                  ) : (
                                    <div className="w-20 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                                      <ImageIcon size={20} className="text-gray-400" />
                                    </div>
                                  )}
                                </div>

                                {/* Plate Image */}
                                <div className="flex flex-col items-center">
                                  <div className="text-xs text-gray-500 mb-1">Plate</div>
                                  {record.plateImageEntry ? (
                                    <img
                                      src={getImageUrl(record.plateImageEntry, 'plate')}
                                      alt="Plate Entry"
                                      className="w-20 h-14 object-cover rounded-lg bg-gray-200 cursor-pointer"
                                      onError={handleImageError}
                                      onClick={() => setPreviewImage(getImageUrl(record.plateImageEntry, 'plate'))}
                                    />
                                  ) : (
                                    <div className="w-20 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                                      <ImageIcon size={20} className="text-gray-400" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Column 3: Plate Details */}
                            <td className="px-4 py-4 align-top">
                              <div className="space-y-2">
                                <div>
                                  <div className="text-sm font-semibold text-gray-800">
                                    {record.plateNumber || "Unknown"}
                                  </div>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPlateTypeColor(record.plateType)}`}>
                                      {record.plateType}
                                    </span>
                                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record.plateStatus)}`}>
                                      {record.plateStatus || "Unclassified"}
                                    </span>
                                  </div>
                                </div>

                                <div className="text-xs text-gray-600">
                                  <div><span className="font-medium">Region:</span> {record.region || "Unknown"}</div>
                                  <div className={`font-medium ${getConfidenceColor(record.confidence)}`}>
                                    Confidence: {record.confidence === null || record.confidence === 0
                                      ? "N/A"
                                      : record.confidence.toFixed(1) + "%"}
                                  </div>
                                </div>
                              </div>
                            </td>
                            {/* Column 4: Vehicle Info */}
                            <td className="px-4 py-4 align-top text-sm text-gray-600">
                              <div><span className="font-medium">Type:</span> {record.vehicleType || "Unknown"}</div>
                              {record.processing_time && (
                                <div><span className="font-medium">Proc. Time:</span> {record.processing_time}</div>
                              )}
                              {record.image_resolution && (
                                <div><span className="font-medium">Resolution:</span> {record.image_resolution}</div>
                              )}
                            </td>

                            {/* Column 5: Actions */}
                            <td className="px-4 py-4 align-top text-center relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleMenu(record.id);
                                }}
                                aria-label="Open actions menu"
                                className="p-1.5 rounded-full hover:bg-gray-100"
                              >
                                <MoreVertical size={18} />
                              </button>

                              {menuOpenId === record.id.toString() && (
                                <div
                                  className={`absolute w-40 bg-white rounded-md shadow-lg border border-gray-200 z-20 ${shouldOpenUpward(index) ? "bottom-0 right-10" : "top-0 right-10"
                                    }`}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    className="flex items-center w-full text-left px-4 py-2 text-sm hover:bg-gray-100 gap-x-2"
                                    onClick={() => {
                                      setEditModal({ open: true, record });
                                      setMenuOpenId(null);
                                    }}
                                  >
                                    <Edit className="w-4 h-4" />
                                    <span>Edit</span>
                                  </button>
                                  <button
                                    className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-100 gap-x-2 border-t border-gray-200"
                                    onClick={() => handleDeleteClick(record)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    <span>Delete</span>
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  // MOBILE CARD VIEW
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-4 p-4">
                    {detectionRecords.map((record) => (
                      <div key={record.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                        {/* Card Header with Plate Number and Actions */}
                        <div className="border-b border-gray-100 px-4 py-3 flex justify-between items-center bg-gray-50">
                          <h3 className="font-medium text-gray-900">
                            {record.plateNumber || "Unknown Plate"}
                          </h3>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu(record.id);
                              }}
                              aria-label="Actions"
                              className="p-1.5 rounded-full hover:bg-gray-200"
                            >
                              <MoreVertical size={18} />
                            </button>

                            {menuOpenId === record.id.toString() && (
                              <div
                                className="absolute right-0 top-8 w-40 bg-white rounded-md shadow-lg border border-gray-200 z-20"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  className="flex items-center w-full text-left px-4 py-2 text-sm hover:bg-gray-100 gap-x-2"
                                  onClick={() => {
                                    setEditModal({ open: true, record });
                                    setMenuOpenId(null);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                  <span>Edit</span>
                                </button>
                                <button
                                  className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-100 gap-x-2 border-t border-gray-200"
                                  onClick={() => handleDeleteClick(record)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  <span>Delete</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Card Body */}
                        <div className="p-4">
                          {/* Images Row */}
                          <div className="flex justify-center gap-3 mb-3">
                            {/* Annotated Image */}
                            <div className="flex flex-col items-center">
                              <div className="text-xs text-gray-500 mb-1">Annotated</div>
                              {record.annotatedImageEntry ? (
                                <img
                                  src={getImageUrl(record.annotated_path, 'annotated')}
                                  alt="Annotated"
                                  className="w-20 h-14 object-cover rounded-lg bg-gray-200 cursor-pointer"
                                  onError={(e) => handleImageError(e, 'annotated-entry')}
                                  onClick={() => setPreviewImage(getImageUrl(record.annotated_path, 'annotated'))}
                                />
                              ) : (
                                <div className="w-20 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                                  <ImageIcon size={20} className="text-gray-400" />
                                </div>
                              )}
                            </div>

                            {/* Plate Image */}
                            <div className="flex flex-col items-center">
                              <div className="text-xs text-gray-500 mb-1">Plate</div>
                              {record.plateImageEntry ? (
                                <img
                                  src={getImageUrl(record.plateImageEntry, 'plate')}
                                  alt="Plate"
                                  className="w-20 h-14 object-cover rounded-lg bg-gray-200 cursor-pointer"
                                  onError={handleImageError}
                                  onClick={() => setPreviewImage(getImageUrl(record.plateImageEntry, 'plate'))}
                                />
                              ) : (
                                <div className="w-20 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                                  <ImageIcon size={20} className="text-gray-400" />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Details Grid */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div className="flex flex-col">
                              <span className="text-gray-500 text-xs">Timestamp</span>
                              <span className="text-gray-800">{record.timestampEntry}</span>
                            </div>

                            <div className="flex flex-col">
                              <span className="text-gray-500 text-xs">Vehicle Type</span>
                              <span className="text-gray-800">{record.vehicleType || "Unknown"}</span>
                            </div>

                            <div className="flex flex-col">
                              <span className="text-gray-500 text-xs">Region</span>
                              <span className="text-gray-800">{record.region || "Unknown"}</span>
                            </div>

                            <div className="flex flex-col">
                              <span className="text-gray-500 text-xs">Confidence</span>
                              <span className={getConfidenceColor(record.confidence)}>
                                {record.confidence === null || record.confidence === 0
                                  ? "N/A"
                                  : record.confidence.toFixed(1) + "%"}
                              </span>
                            </div>
                          </div>

                          {/* Tags Row */}
                          <div className="flex flex-wrap gap-2 mt-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlateTypeColor(record.plateType)}`}>
                              {record.plateType}
                            </span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(record.plateStatus)}`}>
                              {record.plateStatus || "Unclassified"}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* PAGINATION */}
              {!loading && !error && detectionRecords.length > 0 && (
                <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-b-2xl p-4 md:p-6 shadow-lg">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-sm text-gray-600 text-center sm:text-left">
                      Showing{" "}
                      {totalEntries === 0
                        ? 0
                        : (currentPage - 1) * recordsPerPage + 1}{" "}
                      to {Math.min(currentPage * recordsPerPage, totalEntries)}{" "}
                      of {totalEntries} records
                    </p>

                    <div className="flex items-center gap-1 sm:gap-2">
                      {/* First Page Button - Hide on mobile */}
                      <button
                        onClick={goToFirstPage}
                        disabled={currentPage === 1}
                        className={`hidden sm:flex items-center px-2 py-1 text-sm rounded ${currentPage === 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-100"
                          }`}
                        title="First Page"
                      >
                        <ChevronsLeft size={16} />
                      </button>

                      {/* Previous Page Button */}
                      <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`flex items-center px-2 sm:px-3 py-1 text-sm rounded ${currentPage === 1
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        <ChevronLeft size={16} />
                        <span className="sr-only sm:not-sr-only sm:ml-1">Previous</span>
                      </button>

                      {/* Page Numbers - Simplified on mobile */}
                      <div className="flex items-center">
                        {/* Show reduced pagination on small screens */}
                        <div className="sm:hidden px-2 py-1 text-sm text-gray-700">
                          Page {currentPage} of {totalPages}
                        </div>

                        {/* Full pagination on larger screens */}
                        <div className="hidden sm:flex items-center">
                          {pageNumbers.map((pageNum, index) => {
                            const uniqueKey = `page-${pageNum}-pos-${index}`;

                            // Special rendering for ellipsis
                            if (pageNum < 0) {
                              return (
                                <span key={uniqueKey} className="px-2 py-1 text-gray-500">…</span>
                              );
                            }

                            // Regular page numbers
                            return (
                              <button
                                key={uniqueKey}
                                onClick={() => goToPage(pageNum)}
                                className={`flex items-center px-3 py-1 text-sm rounded ${currentPage === pageNum
                                  ? "bg-blue-500 text-white"
                                  : "text-gray-600 hover:bg-gray-100"
                                  }`}
                                title={`Page ${pageNum}`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Next Page Button */}
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`flex items-center px-2 sm:px-3 py-1 text-sm rounded ${currentPage === totalPages
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        <span className="sr-only sm:not-sr-only sm:mr-1">Next</span>
                        <ChevronRight size={16} />
                      </button>

                      {/* Last Page Button - Hide on mobile */}
                      <button
                        onClick={goToLastPage}
                        disabled={currentPage === totalPages}
                        className={`hidden sm:flex items-center px-2 py-1 text-sm rounded ${currentPage === totalPages
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-100"
                          }`}
                        title="Last Page"
                      >
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="text-center py-4">
                <p className="text-gray-500 text-opacity-80 text-sm">
                  &copy;2025, Galaxy Dev
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal((prev) => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        recordId={confirmModal.recordId}
        useDirectApi={confirmModal.useDirectApi}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Image Preview Modal */}
      <ImagePreviewModal
        isOpen={!!previewImage}
        imageUrl={previewImage}
        onClose={() => setPreviewImage(undefined)}
      />

      {/* Edit Detail Modal */}
      <EditDetailModal
        isOpen={editModal.open}
        record={editModal.record}
        onClose={() => setEditModal({ open: false, record: null })}
        onUpdate={handleRecordUpdate}
      />
      <TechnoArtLogo />
    </div>
  );
};

export default DetectionHistoryPage;
